import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CogIcon, 
  ShieldCheckIcon, 
  PuzzlePieceIcon, 
  SparklesIcon,
  ArrowRightIcon 
} from '@heroicons/react/24/outline';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PricingPlan from '../components/PricingPlan';

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [hoveredFeature, setHoveredFeature] = useState(0);
  const [isVideoModalOpen, setIsVideoModalOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('checkin');
  const [currentBolingbrookImage, setCurrentBolingbrookImage] = useState(1);
  const [openFAQ, setOpenFAQ] = useState(1);
  const cfSectionRef = useRef<HTMLElement>(null);

  const scrollToCF = () => {
    cfSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  
  const showSection = (sectionName: string) => {
    setActiveSection(sectionName);
  };

  const nextSection = () => {
    const sections = ['checkin', 'scramble', 'social', 'auction'];
    const currentIndex = sections.indexOf(activeSection);
    const nextIndex = (currentIndex + 1) % sections.length;
    setActiveSection(sections[nextIndex]);
  };

  const toggleFAQ = (faqNumber: number) => {
    setOpenFAQ(openFAQ === faqNumber ? 0 : faqNumber);
  };
  
  // Handle Escape key to close modal
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsVideoModalOpen(false);
      }
    };

    if (isVideoModalOpen) {
      document.addEventListener('keydown', handleEscape);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isVideoModalOpen]);

  // Rotate Bolingbrook background images every 6 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentBolingbrookImage(prev => (prev % 4) + 1);
    }, 6000);

    return () => clearInterval(interval);
  }, []);
  
  const scrollToPricing = () => {
    const pricingSection = document.getElementById('pricing-section');
    if (pricingSection) {
      pricingSection.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  useEffect(() => {
    const shouldScrollToPricing = (location.state as { scrollToPricing?: boolean } | null)?.scrollToPricing;
    if (!shouldScrollToPricing) {
      return;
    }

    const timer = window.setTimeout(() => {
      const pricingSection = document.getElementById('pricing-section');
      if (pricingSection) {
        pricingSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
      navigate(location.pathname, { replace: true, state: null });
    }, 150);

    return () => {
      window.clearTimeout(timer);
    };
  }, [location.pathname, location.state, navigate]);

  const scrollToCheckIn = () => {
    showSection('checkin');
    const section = document.getElementById('dynamic-section');
    if (section) {
      section.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const scrollToSocialHour = () => {
    showSection('social');
    const section = document.getElementById('dynamic-section');
    if (section) {
      section.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const scrollToScramble = () => {
    showSection('scramble');
    const section = document.getElementById('dynamic-section');
    if (section) {
      section.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };

  const scrollToAuction = () => {
    showSection('auction');
    const section = document.getElementById('dynamic-section');
    if (section) {
      section.scrollIntoView({ 
        behavior: 'smooth',
        block: 'start'
      });
    }
  };
  
  return (
    <div className="min-h-screen bg-black">
      <Navigation />
      
      {/* Golf Landing Page - Exact Design Match */}
      <section className="relative min-h-screen overflow-hidden">
        
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
            style={{
              backgroundImage: `url(${process.env.PUBLIC_URL}/assets/cc-challenge-coin-golf-bg.png)`
            }}
          ></div>
          <div className="absolute inset-0 bg-black bg-opacity-70"></div>
        </div>

        {/* Champions Classic Logo - Right Side */}
        <div 
          className="absolute top-[110px] right-8 lg:right-16 z-20 hidden md:block hero-1023-logo hero-desktop-right-block"
        >
          <img 
            src={`${process.env.PUBLIC_URL}/assets/ChampionsClassicLogo-Final-White.png`} 
            alt="Champions Classic Logo" 
            className="object-contain drop-shadow-2xl hero-desktop-logo-img right-8"
            style={{ width: '200px', height: '200px', marginLeft: '50px' }}
          />
          
          {/* Address - Desktop Only */}
          <div className="hidden lg:block mt-2 text-center relative z-30 hero-desktop-address">
            <p className="text-white text-lg font-semibold drop-shadow-lg">
              BOLINGBROOK GOLF CLUB
            </p>
            <p className="text-white text-sm font-medium drop-shadow-lg mt-0">
              SEPTEMBER 10, 2026
            </p>
            <p className="text-white text-base drop-shadow-lg mt-0 leading-relaxed">
              2001 Rodeo Drive, 
              Bolingbrook, IL 60490
            </p>
            <button onClick={scrollToCF} className="focus:outline-none cursor-pointer hero-desktop-cf" style={{ marginTop: '30px' }}>
              <div className="flex items-center justify-center gap-3">
                <p className="text-white font-semibold italic opacity-75 whitespace-nowrap" style={{ fontSize: '13px' }}>BENEFITTING</p>
                <img
                  src={`${process.env.PUBLIC_URL}/assets/cf-logo-3.png`}
                  alt="Crash Champions Foundation"
                  className="h-12 w-auto opacity-90 hover:opacity-100 transition-opacity hero-desktop-cf-logo"
                />
              </div>
            </button>
          </div>
        </div>

        {/* 1023px Tablet Side Panels */}
        <div className="hero-1023-side hero-1023-left hidden">
          <p className="text-white text-lg font-semibold drop-shadow-lg">
            BOLINGBROOK GOLF CLUB
          </p>
          <p className="text-white text-sm font-medium drop-shadow-lg mt-1">
            SEPTEMBER 10, 2026
          </p>
          <p className="text-white text-base drop-shadow-lg mt-0 leading-relaxed">
            2001 Rodeo Drive, 
            Bolingbrook, IL 60490
          </p>
        </div>

        <div className="hero-1023-side hero-1023-right hidden" style={{ marginTop: '-10px' }}>
          <button onClick={scrollToCF} className="focus:outline-none cursor-pointer">
            <div className="flex items-center justify-center gap-3">
              <p className="text-white font-semibold italic opacity-75 whitespace-nowrap" style={{ fontSize: '16px' }}>BENEFITTING</p>
              <img
                src={`${process.env.PUBLIC_URL}/assets/cf-logo-3.png`}
                alt="Crash Champions Foundation"
                className="h-12 w-auto opacity-90 hover:opacity-100 transition-opacity"
                style={{ marginLeft: '90px' }}
              />
            </div>
          </button>
        </div>

        {/* Main Content - Left Side */}
        <div className="hold-space relative z-20 flex items-center min-h-screen -mt-24 lg:mt-0 -mt-14 lg:mt-0 pointer-events-none" style={{ paddingTop: '75px' }}>
          <div className="max-w-12xl mx-auto pl-8 pr-4 sm:px-6 lg:px-8 w-full pointer-events-none">
            <div className="max-w-3xl pointer-events-auto mt-12 hero-1023-content">

              {/* Main Headline */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="md:hidden flex justify-center mb-2"
              >
                <img
                  src={`${process.env.PUBLIC_URL}/assets/ChampionsClassicLogo-Final-White.png`}
                  alt="Champions Classic Logo"
                  className="object-contain drop-shadow-2xl"
                  style={{ width: '102px', height: '102px' }}
                />
              </motion.div>

              <motion.h1 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="hero-headline text-center md:text-left mt-0 md:mt-[-60px] font-bold text-white mb-12 leading-none lg:leading-tight text-[29px] md:text-[42px] lg:text-[56px]"
              >
                Champions playing with <br className="md:hidden" /><span className="whitespace-nowrap">purpose and impact</span>
              </motion.h1>

              {/* CTA Button */}
              <motion.div
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="get-started-button flex justify-center md:justify-start"
              >
                <button
                  onClick={scrollToPricing}
                  className="inline-flex items-center gap-3 text-white px-7 py-3 rounded-full font-bold text-base transition-all duration-300 shadow-lg cursor-pointer"
                  style={{backgroundColor: '#e51c22'}}
                >
                  Register Now
                  <ArrowRightIcon className="w-5 h-5" />
                </button>
              </motion.div>

              {/* BENEFITTING Section - Mobile Only */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                className="md:hidden flex justify-center mt-8 mb-6"
              >
                <button onClick={scrollToCF} className="focus:outline-none">
                  <div className="flex items-center justify-center gap-2">
                    <p className="text-white font-semibold italic opacity-75 whitespace-nowrap" style={{ fontSize: '11px' }}>BENEFITTING</p>
                    <img
                      src={`${process.env.PUBLIC_URL}/assets/cf-logo-3.png`}
                      alt="Crash Champions Foundation"
                      className="h-10 w-auto opacity-90 hover:opacity-100 transition-opacity"
                    />
                  </div>
                </button>
              </motion.div>

              {/* Address - iPad and Mobile Only */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="lg:hidden text-center address-mobile z-30 mt-6 mb-12 md:mb-0 pb-12 md:pb-0 hero-1023-mobile-address"
              >
                <p className="text-white font-semibold drop-shadow-lg text-sm md:text-base">
                  BOLINGBROOK GOLF CLUB
                </p>
                <p className="text-white drop-shadow-lg mt-1 text-xs md:text-sm">
                  SEPTEMBER 10, 2026
                </p>
                <p className="text-white drop-shadow-lg mt-1 text-xs md:text-sm">
                  2001 Rodeo Drive | 
                  Bolingbrook, IL 60490
                </p>
              </motion.div>

            </div>
          </div>
        </div>

        {/* Bottom 4 Features Section - Desktop */}
        <div className="hold-space hidden lg:block absolute bottom-0 left-0 right-0 z-20 hero-feature-panel">
          <div className="max-w-12xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            
            {/* Faded Horizontal Line */}
            <div className="relative mb-8">
              <div className="w-full h-px bg-gray-600 opacity-30"></div>
              
              {/* Animated Red Highlight Line */}
              <motion.div
                className="absolute top-0 h-1 hero-feature-hover-line"
                style={{backgroundColor: '#e51c22'}}
                initial={{ width: '25%', x: '0%' }}
                animate={{ 
                  x: `${hoveredFeature * 100}%`,
                  width: '25%'
                }}
                transition={{ duration: 0.3, ease: 'easeInOut' }}
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              
              {/* Check-In */}
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.6 }}
                className="text-left cursor-pointer hero-feature-card"
                onMouseEnter={() => setHoveredFeature(0)}
                onClick={scrollToCheckIn}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 hero-feature-icon" style={{backgroundColor: '#e51c22'}}>
                    <ShieldCheckIcon className="w-6 h-6 text-white hero-feature-icon-svg" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white hero-feature-title">Check-In</h3>
                    <p className="text-sm text-gray-300 font-bold hero-feature-time">8:00AM</p>
                  </div>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed hero-feature-body">
                  All players who are participating MUST check-in by <b>8:00AM</b>.
                </p>
              </motion.div>

              {/* Scramble/Shotgun Start */}  
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.7 }}
                className="text-left cursor-pointer hero-feature-card"
                onMouseEnter={() => setHoveredFeature(1)}
                onClick={scrollToScramble}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 hero-feature-icon" style={{backgroundColor: '#e51c22'}}>
                    <CogIcon className="w-6 h-6 text-white hero-feature-icon-svg" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white hero-feature-title">Scramble/Shotgun Start</h3>
                    <p className="text-sm text-gray-300 font-bold hero-feature-time">10:00AM</p>
                  </div>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed hero-feature-body">
                  All players will begin their round at <b>10:00AM</b>.
                </p>
              </motion.div>

              {/* Social Hour */}
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="text-left cursor-pointer hero-feature-card"
                onMouseEnter={() => setHoveredFeature(2)}
                onClick={scrollToSocialHour}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 hero-feature-icon" style={{backgroundColor: '#e51c22'}}>
                    <PuzzlePieceIcon className="w-6 h-6 text-white hero-feature-icon-svg" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white hero-feature-title">Social Hour</h3>
                    <p className="text-sm text-gray-300 font-bold hero-feature-time">3:00PM</p>
                  </div>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed hero-feature-body">
                  Social hour will begin at <b>3:00PM</b>.
                </p>
              </motion.div>

              {/* Auction/Raffle Dinner */}
              <motion.div 
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.9 }}
                className="text-left cursor-pointer hero-feature-card"
                onMouseEnter={() => setHoveredFeature(3)}
                onClick={scrollToAuction}
              >
                <div className="flex items-center gap-4 mb-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 hero-feature-icon" style={{backgroundColor: '#e51c22'}}>
                    <SparklesIcon className="w-6 h-6 text-white hero-feature-icon-svg" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white hero-feature-title">Auction/Raffle Dinner</h3>
                    <p className="text-sm text-gray-300 font-bold hero-feature-time">4:00PM</p>
                  </div>
                </div>
                <p className="text-gray-300 text-sm leading-relaxed hero-feature-body">
                  Post-round auction and raffle begins at 4:00PM.
                </p>
              </motion.div>

            </div>
          </div>
        </div>

      </section>

      {/* Mobile Features Section - Below Hero */}
      <section 
        className="lg:hidden bg-black py-12 relative z-30"
        style={{ marginTop: '-214px' }}
      >
        <div className="max-w-12xl mx-auto pl-8 pr-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            
            {/* Check-In */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              viewport={{ once: true }}
              className="text-left"
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#e51c22'}}>
                  <ShieldCheckIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Check-In</h3>
                  <p className="text-sm text-gray-300 font-bold">8:00AM</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                All players who are participating MUST check-in by <b>8:00AM</b>.
              </p>
            </motion.div>

            {/* Scramble/Shotgun Start */}  
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="text-left"
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#e51c22'}}>
                  <CogIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Scramble/Shotgun Start</h3>
                  <p className="text-sm text-gray-300 font-bold">10:00AM</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                All players will begin their round at <b>10:00AM</b>.
              </p>
            </motion.div>

            {/* Social Hour */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-left"
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#e51c22'}}>
                  <PuzzlePieceIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Social Hour</h3>
                  <p className="text-sm text-gray-300 font-bold">3:00PM</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                Social hour will begin at <b>3:00PM</b>.
              </p>
            </motion.div>

            {/* Auction/Raffle Dinner */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="text-left cursor-pointer"
              onClick={scrollToAuction}
            >
              <div className="flex items-center gap-4 mb-3">
                <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0" style={{backgroundColor: '#e51c22'}}>
                  <SparklesIcon className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">Auction/Raffle Dinner</h3>
                  <p className="text-sm text-gray-300 font-bold">4:00PM</p>
                </div>
              </div>
              <p className="text-gray-300 text-sm leading-relaxed">
                Post-round auction and raffle begins at 4:00PM.
              </p>
            </motion.div>

          </div>
        </div>
      </section>
      
      {/* Pricing Plan Section */}
      <div id="pricing-section">
        <PricingPlan />
      </div>
      
      {/* Dynamic Section - Shows Different Content Based on Hero Click */}
      <section id="dynamic-section" className="relative bg-white py-20 px-4 sm:px-6 lg:px-8 overflow-hidden">
        <div className="mx-auto" style={{maxWidth: '100rem'}}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            
            {/* Left Content - Dynamic Based on Active Section */}
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                key={activeSection} // Force re-animation when section changes
              >
                {activeSection === 'checkin' && (
                  <>
                    <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 block">
                      CHECK-IN | 8:00AM
                    </span>
                    <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                      Registration,<br />
                      Powered by Champions
                    </h2>
                    <p className="text-gray-600 text-lg leading-relaxed mb-12 max-w-md">
                      Our streamlined registration experience, driven by Champions Classic, ensures a smooth check-in and efficient player flow from start to finish. Backed by a dedicated team and a commitment to excellence, we deliver a seamless arrival experience for every guest on tournament day.
                    </p>
                  </>
                )}
                
                {activeSection === 'social' && (
                  <>
                    <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 block">
                      SOCIAL HOUR | 3:00PM
                    </span>
                    <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                      Unwind. Connect.<br />
                      Celebrate.
                    </h2>
                    <p className="text-gray-600 text-lg leading-relaxed mb-12 max-w-md">
                      After the final putt drops, the experience continues at our social hour. Connect with fellow golfers, relive the highlights of the round, and enjoy a relaxed setting filled with great conversation, refreshments, and camaraderie.
                    </p>
                  </>
                )}
                
                {activeSection === 'scramble' && (
                  <>
                    <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 block">
                      SCRAMBLE/SHOTGUN START | 10:00AM
                    </span>
                    <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                      All Teams. One Start.<br />
                      Pure Competition.
                    </h2>
                    <p className="text-gray-600 text-lg leading-relaxed mb-12 max-w-md">
                      Our scramble format kicks off with a shotgun start, placing every team in position across the course simultaneously. The result is an exciting, unified round that brings everyone back together just in time for networking, refreshments, and awards.
                    </p>
                  </>
                )}
                
                {activeSection === 'auction' && (
                  <>
                    <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-4 block">
                      AUCTION/RAFFLE DINNER | 4:00PM
                    </span>
                    <h2 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                      An Evening<br />
                      That Gives Back
                    </h2>
                    <p className="text-gray-600 text-lg leading-relaxed mb-12 max-w-md">
                      The Champions Classic auction, raffle, and dinner bring the day to a memorable close with great food, exciting bidding, and meaningful impact. Guests can enjoy a premium dining experience while supporting charitable initiatives that make a difference in our communities.
                    </p>
                  </>
                )}
                
                <div className="flex items-center gap-4">
                  <button 
                    className="inline-flex items-center gap-3 px-7 py-3 text-white font-semibold rounded-full transition-all duration-300 hover:scale-105 shadow-lg"
                    style={{backgroundColor: '#e51c22'}}
                    onClick={scrollToPricing}
                  >
                    Register Now
                    <ArrowRightIcon className="w-5 h-5" />
                  </button>
                  
                  <button 
                    className="inline-flex items-center gap-2 px-6 py-4 text-gray-700 font-semibold rounded-full transition-all duration-300 hover:scale-105 shadow-lg border-2 border-gray-300 hover:border-gray-400 bg-white"
                    onClick={nextSection}
                  >
                    NEXT
                    <ArrowRightIcon className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            </div>

            {/* Right Content - Dynamic Images Based on Active Section */}
            <div className="relative">
              
              {/* Desktop Layout - Extra large screens (xl+) */}
              <div className="hidden xl:block">
                {/* Large Center Image - Desktop Float Left */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="relative float-left"
                  style={{ transform: 'translateX(-40px)' }}
                  key={`main-desktop-${activeSection}`}
                >
                  <div className="w-[420px] h-[500px] rounded-3xl overflow-hidden shadow-2xl relative" style={{ transform: 'translateX(-40px)' }}>
                    <img 
                      src={
                        activeSection === 'checkin' ? `${process.env.PUBLIC_URL}/assets/SKA05593_1.jpg` :
                        activeSection === 'social' ? `${process.env.PUBLIC_URL}/assets/SKA05635_1.jpg` :
                        activeSection === 'scramble' ? `${process.env.PUBLIC_URL}/assets/SKA05645_1.jpg` :
                        `${process.env.PUBLIC_URL}/assets/golf-pics/SKA05584.JPG`
                      }
                      alt="Golf experience" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </motion.div>

                {/* Right Images Stack - Desktop */}
                <div className="space-y-6 ml-auto" style={{width: '340px'}}>
                  {/* Top Right Image */}
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="rounded-2xl overflow-hidden shadow-xl"
                    key={`top-desktop-${activeSection}`}
                  >
                    <div className="h-[238px]">
                      <img 
                        src={
                          activeSection === 'checkin' ? `${process.env.PUBLIC_URL}/assets/golf-pics/SKA05645.JPG` :
                          activeSection === 'social' ? `${process.env.PUBLIC_URL}/assets/golf-pics/SKA03758.JPG` :
                          activeSection === 'scramble' ? `${process.env.PUBLIC_URL}/assets/golf-pics/SKA03684.JPG` :
                          `${process.env.PUBLIC_URL}/assets/golf-pics/SKA03733.JPG`
                        }
                        alt="Golf experience" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </motion.div>

                  {/* Bottom Right Image */}
                  <motion.div
                    initial={{ opacity: 0, x: 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    viewport={{ once: true }}
                    className="rounded-2xl overflow-hidden shadow-xl"
                    key={`bottom-desktop-${activeSection}`}
                  >
                    <div className="h-[238px]">
                      <img 
                        src={
                          activeSection === 'checkin' ? `${process.env.PUBLIC_URL}/assets/golf-pics/SKA03735.JPG` :
                          activeSection === 'social' ? `${process.env.PUBLIC_URL}/assets/golf-pics/SKA03733.JPG` :
                          activeSection === 'scramble' ? `${process.env.PUBLIC_URL}/assets/golf-pics/SKA05566.JPG` :
                          `${process.env.PUBLIC_URL}/assets/golf-pics/SKA03696.JPG`
                        }
                        alt="Golf experience" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Tablet/Mobile Layout - Large and smaller screens */}
              <div className="block xl:hidden">
                {/* Big Image on Top - Tablet/Mobile */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.8, delay: 0.2 }}
                  viewport={{ once: true }}
                  className="w-full mb-6"
                  key={`main-tablet-${activeSection}`}
                >
                  <div className="w-full h-64 md:h-80 rounded-3xl overflow-hidden shadow-2xl">
                    <img 
                      src={
                        activeSection === 'checkin' ? `${process.env.PUBLIC_URL}/assets/SKA05593_1.jpg` :
                        activeSection === 'social' ? `${process.env.PUBLIC_URL}/assets/SKA05635_1.jpg` :
                        activeSection === 'scramble' ? `${process.env.PUBLIC_URL}/assets/SKA05645_1.jpg` :
                        `${process.env.PUBLIC_URL}/assets/golf-pics/SKA05584.JPG`
                      }
                      alt="Golf experience" 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </motion.div>

                {/* Two Images Side by Side - Tablet/Mobile */}
                <div className="flex gap-4">
                  {/* Left Image */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="flex-1 rounded-2xl overflow-hidden shadow-xl"
                    key={`left-tablet-${activeSection}`}
                  >
                    <div className="h-48 md:h-56">
                      <img 
                        src={
                          activeSection === 'checkin' ? `${process.env.PUBLIC_URL}/assets/golf-pics/SKA05645.JPG` :
                          activeSection === 'social' ? `${process.env.PUBLIC_URL}/assets/golf-pics/SKA03758.JPG` :
                          activeSection === 'scramble' ? `${process.env.PUBLIC_URL}/assets/golf-pics/SKA03684.JPG` :
                          `${process.env.PUBLIC_URL}/assets/golf-pics/SKA03733.JPG`
                        }
                        alt="Golf experience" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </motion.div>

                  {/* Right Image */}
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8, delay: 0.4 }}
                    viewport={{ once: true }}
                    className="flex-1 rounded-2xl overflow-hidden shadow-xl"
                    key={`right-tablet-${activeSection}`}
                  >
                    <div className="h-48 md:h-56">
                      <img 
                        src={
                          activeSection === 'checkin' ? `${process.env.PUBLIC_URL}/assets/golf-pics/SKA03735.JPG` :
                          activeSection === 'social' ? `${process.env.PUBLIC_URL}/assets/golf-pics/SKA03733.JPG` :
                          activeSection === 'scramble' ? `${process.env.PUBLIC_URL}/assets/golf-pics/SKA05566.JPG` :
                          `${process.env.PUBLIC_URL}/assets/golf-pics/SKA03696.JPG`
                        }
                        alt="Golf experience" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </motion.div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Video Section - Golf Experience with Rotating Background */}
      <section className="relative min-h-[45vh] md:min-h-[25vh] lg:min-h-[30vh] xl:min-h-[60vh] flex items-center justify-center overflow-hidden">
        {/* Rotating Background Images */}
        {[1, 2, 3, 4].map((imageNum) => (
          <div
            key={imageNum}
            className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-1000 ${
              currentBolingbrookImage === imageNum ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              backgroundImage: `url('${process.env.PUBLIC_URL}/assets/Bolingbrook-${imageNum}.jpg')`
            }}
          />
        ))}
        
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-black bg-opacity-50 z-10"></div>
        
        {/* Content Container */}
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="flex flex-col items-center justify-center text-center">
            
            {/* Small "Visit" Text */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="mb-4"
            >
              <span className="text-white font-semibold text-lg sm:text-xl lg:text-2xl uppercase tracking-wider opacity-80">View</span>
            </motion.div>

            {/* Main Heading - Centered */}
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-[2.5rem] leading-none sm:text-6xl sm:leading-tight lg:text-7xl xl:text-8xl font-bold text-white">
                BOLINGBROOK
              </h2>
              <h3 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-white leading-tight mt-2">
                Golf Club
              </h3>
            </motion.div>

            {/* Play Button - Centered */}
            <motion.div 
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              viewport={{ once: true }}
              className="flex justify-center"
            >
              <button 
                className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-full flex items-center justify-center transition-all duration-300 hover:scale-105 shadow-2xl group"
                style={{backgroundColor: 'black', borderWidth: '5px', borderColor: 'white'}}
                aria-label="Play video"
                onClick={() => setIsVideoModalOpen(true)}
              >
                {/* Play Icon */}
                <svg 
                  className="w-6 h-6 sm:w-8 sm:h-8 lg:w-10 lg:h-10 text-white ml-1 group-hover:scale-110 transition-transform duration-300" 
                  fill="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path d="M8 5v14l11-7z"/>
                </svg>
              </button>
            </motion.div>

          </div>
        </div>
      </section>
      
      {/* FAQ Section */}
      <section className="hidden relative bg-white py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            
            {/* Left Content */}
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider mb-4 block">FAQ'S</span>
                
                <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-8">
                  FAQs about<br />
                  Champions Classic golf tournament
                </h2>
              </motion.div>

              {/* Golf Image */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
                className="w-full h-48"
              >
                <img 
                  src={`${process.env.PUBLIC_URL}/assets/scramble-1.jpg`} 
                  alt="Golf tournament" 
                  className="w-full h-full object-cover rounded-2xl shadow-lg"
                />
              </motion.div>

            </div>

            {/* Right Content - FAQs */}
            <div className="space-y-6">

              {/* FAQ Items */}
              <div className="space-y-4">
                
                {/* FAQ 1 - Expanded by default */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.1 }}
                  viewport={{ once: true }}
                  className={`rounded-2xl p-6 cursor-pointer transition-all duration-300 ${
                    openFAQ === 1 ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'
                  }`}
                  onClick={() => toggleFAQ(1)}
                >
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">1. What format is the tournament?</h3>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      openFAQ === 1 ? 'bg-white text-black' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {openFAQ === 1 ? '−' : '+'}
                    </div>
                  </div>
                  {openFAQ === 1 && (
                    <p className="text-sm leading-relaxed">
                      The Champions Classic features a 4-player team scramble format with a shotgun start. All teams begin simultaneously across the course, ensuring everyone finishes around the same time for our social hour and awards ceremony.
                    </p>
                  )}
                </motion.div>

                {/* FAQ 2 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.2 }}
                  viewport={{ once: true }}
                  className={`rounded-2xl p-6 cursor-pointer transition-all duration-300 ${
                    openFAQ === 2 ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'
                  }`}
                  onClick={() => toggleFAQ(2)}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">2. What's included in registration?</h3>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      openFAQ === 2 ? 'bg-white text-black' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {openFAQ === 2 ? '−' : '+'}
                    </div>
                  </div>
                  {openFAQ === 2 && (
                    <p className="text-sm leading-relaxed mt-4">
                      Registration includes 18-hole 4-player team scramble entry, cart rental, greens fees, range balls, food and beverages during play, post-round social hour, and dinner. Plus contests, prizes, and awards ceremony.
                    </p>
                  )}
                </motion.div>

                {/* FAQ 3 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.3 }}
                  viewport={{ once: true }}
                  className={`rounded-2xl p-6 cursor-pointer transition-all duration-300 ${
                    openFAQ === 3 ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'
                  }`}
                  onClick={() => toggleFAQ(3)}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">3. Can I participate in the auction without playing?</h3>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      openFAQ === 3 ? 'bg-white text-black' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {openFAQ === 3 ? '−' : '+'}
                    </div>
                  </div>
                  {openFAQ === 3 && (
                    <p className="text-sm leading-relaxed mt-4">
                      Absolutely! While we encourage participation in the full tournament experience, the auction and dinner are open to all supporters of our charitable initiatives. Separate dinner tickets are available.
                    </p>
                  )}
                </motion.div>

                {/* FAQ 4 */}
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  viewport={{ once: true }}
                  className={`rounded-2xl p-6 cursor-pointer transition-all duration-300 ${
                    openFAQ === 4 ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'
                  }`}
                  onClick={() => toggleFAQ(4)}
                >
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold">4. What sponsorship opportunities are available?</h3>
                    <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                      openFAQ === 4 ? 'bg-white text-black' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {openFAQ === 4 ? '−' : '+'}
                    </div>
                  </div>
                  {openFAQ === 4 && (
                    <p className="text-sm leading-relaxed mt-4">
                      We offer multiple sponsorship tiers including hole sponsorships, cart sponsorships, and event title sponsorships. Each level provides excellent brand visibility and networking opportunities while supporting our charitable cause.
                    </p>
                  )}
                </motion.div>

              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Champion It Forward Section */}
      <section
        ref={cfSectionRef}
        className="py-24"
        style={{ background: 'linear-gradient(to bottom, #000000 0%, #000000 60%, rgba(229, 28, 34, 0.5) 100%)' }}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-12 items-center">

            {/* Left: Logo */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex flex-col items-center lg:items-start"
            >
              <img
                src={`${process.env.PUBLIC_URL}/assets/cf-logo-3.png`}
                alt="Champion It Forward"
                className="mb-12 w-full max-w-[220px] lg:max-w-[260px] w-auto"
              />

              <div className="mt-6 flex items-start justify-center lg:justify-start gap-4">
                <div className="h-44 w-36 sm:h-44 sm:w-32 rounded-[10px] overflow-hidden shadow-xl -translate-y-4 skew-y-[-4deg]">
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/golf-pics/body-tech-1.png`}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </div>
                <div className="h-44 w-36 sm:h-44 sm:w-32 rounded-[10px] overflow-hidden shadow-xl translate-y-4 skew-y-[-4deg]">
                  <img
                    src={`${process.env.PUBLIC_URL}/assets/golf-pics/body-tech-2.png`}
                    alt=""
                    className="h-full w-full object-cover"
                  />
                </div>
              </div>
            </motion.div>

            {/* Right: Lorem ipsum placeholder text */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.15 }}
            >
              <h2 className="text-3xl font-bold text-white leading-tight mb-5">
                The Future of Collision Repair Depends on People.
              </h2>
              <p className="text-gray-200 text-base leading-relaxed mb-4">
                Vehicles are advancing. The workforce pipeline has not kept pace. Champion It Forward is a nonprofit dedicated to strengthening the collision repair industry through education, training, and workforce development.
              </p>
              <p className="text-gray-200 text-base leading-relaxed mb-4">
                That starts with access. Introducing new talent to the trade. It continues with structure. Supporting mentorship and hands-on learning. And it leads to opportunity. Creating clear paths into long-term careers and providing the tools to succeed.
              </p>
              <p className="text-gray-200 text-base leading-relaxed mb-4">
                This includes partnering with employers at the frontier of training and technology that create consistent, scalable pathways into the industry.
              </p>
              <p className="text-gray-200 text-base leading-relaxed mb-4">
                Because a strong workforce does not happen by chance. It is built with intention and shared responsibility. When people are prepared, the industry moves forward. Standards rise. Safety improves. The future becomes more certain.
              </p>
              <p className="text-gray-200 text-base leading-relaxed mb-6">
                Champion It Forward is supported by partners across the industry, including founding sponsor Crash Champions. Because advancing the industry starts with investing in the people who power it. And that can only happen when we…
              </p>
              <p className="text-base font-bold tracking-wide mb-8" style={{ color: '#e51c22' }}>
                CHAMPION IT FORWARD.
              </p>
              <Link
                to="/cif"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-sm transition-opacity hover:opacity-90"
                style={{ backgroundColor: '#ffffff', color: '#e51c22' }}
              >
                Learn More
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Footer */}
      <Footer />
      
      {/* Video Modal */}
      {isVideoModalOpen && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-75"
          onClick={() => setIsVideoModalOpen(false)}
        >
          <div 
            className="relative max-w-4xl w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={() => setIsVideoModalOpen(false)}
              className="absolute -top-12 right-0 text-white hover:text-gray-300 transition-colors z-60"
              aria-label="Close video"
            >
              <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            {/* Video Player */}
            <div className="relative bg-black rounded-lg overflow-hidden shadow-2xl">
              <video
                className="w-full h-auto"
                controls
                autoPlay
                playsInline
              >
                <source src={`${process.env.PUBLIC_URL}/assets/BolingbrookGolfClub-video.mp4`} type="video/mp4" />
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LandingPage;